import React, { useEffect, useRef, useState } from 'react';
import { Bell, BellRing, BellPlus, X, Share, Send, RefreshCw, Loader2 } from 'lucide-react';
import {
  pushSupported, pushPermission, enablePush, ensurePushIfGranted, iosNeedsInstall, sendTestPush,
} from './pushClient';

const ASKED_KEY = 'inv_push_asked';

/**
 * One-time, non-intrusive prompt to enable Web Push. Respects the browser's
 * permission state: never shows again after grant/deny or after the user
 * dismisses it. If already granted, it silently (re)subscribes this device.
 * On iOS Safari (not yet installed) it shows an "Add to Home Screen" hint.
 */
export function PushPrompt({ onToast }) {
  const [mode, setMode] = useState(null); // 'enable' | 'ios' | null
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported()) {
      // iOS Safari (not installed) doesn't expose PushManager — show install hint once.
      if (iosNeedsInstall() && !localStorage.getItem(ASKED_KEY)) setMode('ios');
      return;
    }
    const perm = pushPermission();
    if (perm === 'granted') {
      ensurePushIfGranted();
      return;
    }
    if (perm === 'denied') return; // respect the browser — never ask again
    if (!localStorage.getItem(ASKED_KEY)) setMode('enable');
  }, []);

  const dismiss = () => { localStorage.setItem(ASKED_KEY, '1'); setMode(null); };

  const onEnable = async () => {
    setBusy(true);
    try {
      const sub = await enablePush();
      localStorage.setItem(ASKED_KEY, '1');
      setMode(null);
      onToast && onToast(sub ? 'Notifications enabled on this device.' : 'Notifications not enabled.');
    } catch (e) {
      localStorage.setItem(ASKED_KEY, '1');
      setMode(null);
      onToast && onToast(e && e.message === 'denied' ? 'Notifications are blocked in browser settings.' : 'Could not enable notifications.');
    } finally {
      setBusy(false);
    }
  };

  if (!mode) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[320] w-[min(92vw,360px)]" data-testid="push-prompt">
      <div className="flex items-start gap-3 rounded-2xl border border-[var(--n-border)] bg-[var(--n-surface)] p-4 shadow-[var(--n-shadow)]">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[var(--n-accent-bg)] text-[var(--n-accent)]">
          <Bell size={17} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-[var(--n-ink)]">Enable notifications</div>
          {mode === 'ios' ? (
            <p className="mt-1 text-[12.5px] leading-relaxed text-[var(--n-muted)]">
              To get push alerts on iPhone, open the <Share size={12} className="inline -mt-0.5" /> Share menu in Safari and choose <span className="font-medium text-[var(--n-ink-soft)]">Add to Home Screen</span>, then open Neutral Tag Domain from there.
            </p>
          ) : (
            <p className="mt-1 text-[12.5px] leading-relaxed text-[var(--n-muted)]">
              Get alerts for shared notes, task assignments, reminders and @mentions — even when Neutral Tag Domain is closed.
            </p>
          )}
          <div className="mt-2.5 flex items-center gap-2">
            {mode === 'enable' && (
              <button onClick={onEnable} disabled={busy} data-testid="push-enable-btn"
                className="n-focus rounded-lg bg-[var(--n-accent)] px-3 py-1.5 text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60">
                {busy ? 'Enabling…' : 'Enable'}
              </button>
            )}
            <button onClick={dismiss} data-testid="push-dismiss-btn"
              className="n-focus rounded-lg px-3 py-1.5 text-[12.5px] font-medium text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)]">
              {mode === 'ios' ? 'Got it' : 'Not now'}
            </button>
          </div>
        </div>
        <button onClick={dismiss} aria-label="Dismiss" className="n-focus -mr-1 -mt-1 shrink-0 rounded-md p-1 text-[var(--n-muted-2)] hover:text-[var(--n-ink)]">
          <X size={15} />
        </button>
      </div>
    </div>
  );
}

export default PushPrompt;

/**
 * Header control to enable / refresh Web Push and self-verify delivery. Opens a
 * small popover so users can (a) enable/refresh push on this device and (b) fire
 * a real "test notification" to confirm it works end-to-end — invaluable on iOS
 * where subscriptions silently rotate. Reflects the live browser permission
 * state. Renders nothing on desktop browsers with no push support at all.
 */
export function PushBell({ onToast }) {
  const [perm, setPerm] = useState(() => (
    pushSupported() ? pushPermission() : (iosNeedsInstall() ? 'ios' : 'unsupported')
  ));
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (perm === 'unsupported') return null;

  const on = perm === 'granted';
  const blocked = perm === 'denied';
  const iosHint = perm === 'ios' || !pushSupported();

  const statusLabel = iosHint ? 'Add to Home Screen to enable'
    : blocked ? 'Blocked in browser settings'
    : on ? 'On for this device' : 'Off';

  const enableOrRefresh = async () => {
    if (iosHint) {
      onToast && onToast('On iPhone/iPad, open the Share menu in Safari and choose “Add to Home Screen”, then open Neutral Tag Domain from the Home Screen icon to enable alerts.');
      return;
    }
    if (blocked) { onToast && onToast('Notifications are blocked. Enable them for this app in your device settings.'); return; }
    setBusy(true);
    try {
      if (perm === 'granted') {
        await ensurePushIfGranted();
        onToast && onToast('This device is registered for notifications.');
      } else {
        const sub = await enablePush();
        setPerm(pushPermission());
        onToast && onToast(sub ? 'Notifications enabled on this device.' : 'Notifications were not enabled.');
      }
    } catch (e) {
      setPerm(pushPermission());
      onToast && onToast(e && e.message === 'denied' ? 'Notifications are blocked in your device settings.' : 'Could not enable notifications.');
    } finally {
      setBusy(false);
    }
  };

  const runTest = async () => {
    if (iosHint) { enableOrRefresh(); return; }
    if (blocked) { onToast && onToast('Notifications are blocked. Enable them for this app in your device settings.'); return; }
    setTesting(true);
    try {
      const res = await sendTestPush();
      setPerm(pushPermission());
      if (!res || res.enabled === false) {
        onToast && onToast('Push is not configured on the server.');
      } else if ((res.devices || 0) === 0) {
        onToast && onToast('No device is registered yet. Tap “Enable on this device”, allow the prompt, then test again.');
      } else if ((res.sent || 0) > 0) {
        onToast && onToast(`Test sent to ${res.sent} device${res.sent > 1 ? 's' : ''}. Check your notifications now.`);
      } else {
        const first = (res.results || [])[0] || {};
        onToast && onToast(`Delivery failed (status ${first.status ?? 'n/a'}). Try “Enable on this device”, then test again.`);
      }
    } catch (e) {
      onToast && onToast('Could not send a test notification.');
    } finally {
      setTesting(false);
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={busy || testing}
        data-testid="push-bell-toggle"
        title={on ? 'Notifications are on — click for options' : 'Enable push notifications'}
        aria-label={on ? 'Push notifications on' : 'Enable push notifications'}
        className={`n-focus inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors hover:bg-[var(--n-canvas-2)] disabled:opacity-60 ${on ? 'text-[var(--n-accent)]' : 'text-[var(--n-ink-soft)]'}`}>
        {busy || testing ? <Loader2 size={17} className="animate-spin" /> : (on ? <BellRing size={17} /> : <BellPlus size={17} />)}
      </button>
      {open && (
        <div data-testid="push-bell-menu"
          className="absolute right-0 z-[320] mt-2 w-64 overflow-hidden rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1.5 shadow-[var(--n-shadow)]">
          <div className="flex items-center gap-2 px-2.5 py-2">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[var(--n-accent-bg)] text-[var(--n-accent)]"><Bell size={15} /></span>
            <span className="min-w-0">
              <span className="block text-[12.5px] font-semibold text-[var(--n-ink)]">Notifications</span>
              <span className="block truncate text-[11.5px] text-[var(--n-muted)]" data-testid="push-status-label">{statusLabel}</span>
            </span>
          </div>
          <button type="button" onClick={enableOrRefresh} disabled={busy || testing} data-testid="push-enable-refresh-btn"
            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[12.5px] text-[var(--n-ink)] transition-colors hover:bg-[var(--n-canvas-2)] disabled:opacity-60">
            {on ? <RefreshCw size={15} className="text-[var(--n-muted)]" /> : <BellPlus size={15} className="text-[var(--n-muted)]" />}
            {on ? 'Refresh this device' : 'Enable on this device'}
          </button>
          <button type="button" onClick={runTest} disabled={busy || testing} data-testid="push-send-test-btn"
            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[12.5px] text-[var(--n-ink)] transition-colors hover:bg-[var(--n-canvas-2)] disabled:opacity-60">
            {testing ? <Loader2 size={15} className="animate-spin text-[var(--n-muted)]" /> : <Send size={15} className="text-[var(--n-muted)]" />}
            Send test notification
          </button>
        </div>
      )}
    </div>
  );
}
